package com.tfc.care.tfccare.ui

import com.tfc.care.tfccare.domain.model.*
import com.tfc.care.tfccare.domain.repository.AppSettings
import com.tfc.care.tfccare.domain.repository.StaffFirebaseService
import com.tfc.care.tfccare.utils.ApiResult
import com.tfc.care.tfccare.utils.Logger
import kotlinx.coroutines.Job
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.catch
import kotlinx.coroutines.flow.update
import kotlinx.coroutines.launch
import kotlin.js.Date

/**
 * ViewModel for the Staff Chat screen.
 * Handles user authentication, observing conversations and messages, sending messages,
 * and managing the overall chat state for the staff member.
 *
 * @property staffService The service used to interact with Firebase for staff-related operations.
 */
class StaffChatViewModel(val staffService: StaffFirebaseService, val appSettings: AppSettings) : BaseViewModel() {

    fun autoLogin() {
        appSettings.apply {
            if (username.isNotEmpty() && pwd.isNotEmpty()) {
                signIn(username, pwd)
            }
        }
    }

    fun checkPersistence() {
        scope.launch {
            _chatState.value = _chatState.value.copy(
                isLoading = true,
                error = null
            )
            staffService.getSavedStaff()
                .catch {
                    _chatState.value = _chatState.value.copy(
                        isLoading = false,
                        error = null
                    )
                    Logger.d("[DEBUG] StaffChatViewModel: checkPersistence error, trying autoLogin")
                    autoLogin()
                }.collect { currentStaff: Staff? ->
                    Logger.d("[DEBUG] StaffChatViewModel: checkPersistence collected staff: ${currentStaff?.id}")
                    if (currentStaff != null && currentStaff.isValid) {
                        handleStaffLoggedIn(currentStaff)
                    } else {
                        Logger.d("[DEBUG] StaffChatViewModel: no saved session, trying autoLogin")
                        autoLogin()
                    }
                    _chatState.value = _chatState.value.copy(isLoading = false)
                }
        }
    }

//    private fun observeStaffPersistence() {
//        scope.launch {
//            staffService.observeCurrentStaff().collect { staff ->
//                Logger.d("[DEBUG] StaffChatViewModel: observeStaffPersistence collected staff: ${staff?.id} (isValid=${staff?.isValid})")
//
//                if (staff != null && staff.isValid) {
//                    // We found a valid staff, so session is restored
//                    _chatState.update { it.copy(isRestoringSession = false) }
//                    if (_staff.value == null || _staff.value?.id != staff.id) {
//                        Logger.d("[DEBUG] StaffChatViewModel: Triggering handleStaffLoggedIn for ${staff.id}")
//                        handleStaffLoggedIn(staff)
//                    }
//                } else if (staff != null && !staff.isValid) {
//                    // Staff found but invalid (unauthorized)
//                    Logger.d("[DEBUG] StaffChatViewModel: observeStaffPersistence - Staff is invalid")
//                    _chatState.update { it.copy(isRestoringSession = false) }
//                    _staff.value = staff
//                } else {
//                    // If we receive null, it might be the initial state OR an actual "no user" from Firebase.
//                    if (_staff.value != null) {
//                        Logger.d("[DEBUG] StaffChatViewModel: Staff logged out or session lost")
//                        _staff.value = null
//                    }
//                }
//            }
//        }
//    }

    private fun handleStaffLoggedIn(staff: Staff) {
        if (_staff.value?.id == staff.id && conversationObservationJob?.isActive == true) {
            return
        }
        _staff.value = staff
        _chatState.value = _chatState.value.copy(isLoading = false, error = null)
        Logger.d("Firebase Request: observing conversations for staffId=${staff.id}")

        observeConversations()
        observeStaffEngagement(staff.id)
    }


    private val _chatState = MutableStateFlow(StaffChatState())

    /**
     * Observable state of the chat, including conversations, messages, and UI status.
     */
    val chatState: StateFlow<StaffChatState> = _chatState.asStateFlow()

    private val _staff = MutableStateFlow<Staff?>(null)

    /**
     * The currently logged-in staff member.
     */
    val staff = _staff.asStateFlow()

    private val _isUserOnline = MutableStateFlow(false)

    /**
     * Indicates whether the selected user is currently online.
     */
    val isUserOnline = _isUserOnline.asStateFlow()

    // Track the current message observation job
    private var messageObservationJob: Job? = null

    // Track the current conversation observation job
    private var conversationObservationJob: Job? = null


    /**
     * Closes the current chat session by nullifying the message observation job.
     */
    fun closeChat() {
        messageObservationJob = null
    }

    /**
     * Marks the staff as inactive by nullifying the conversation observation job.
     */
    fun stayInactive() {
        conversationObservationJob = null
    }

    /**
     * Performs staff sign-in with the provided email and password.
     * On success, starts observing conversations and staff engagement stats.
     *
     * @param email The staff member's email address.
     * @param password The staff member's password.
     */
    fun signIn(email: String, password: String) {
        scope.launch {
            _chatState.value = _chatState.value.copy(isLoading = true, error = null, isRestoringSession = false)
            when (val api = staffService.signInStaff(email, password)) {
                is ApiResult.Success -> {
                    handleStaffLoggedIn(api.data)
                    _chatState.value = _chatState.value.copy(isLoading = false)
                    appSettings.apply {
                        username = email
                        pwd = password
                    }
                }

                is ApiResult.Error -> {
                    _chatState.value = _chatState.value.copy(
                        isLoading = false,
                        error = api.exception.message
                    )
                }

                is ApiResult.Loading -> {
                    _chatState.value = _chatState.value.copy(isLoading = true)
                }
            }
        }
    }

    /**
     * Signs out the current staff member and clears the staff state.
     */
    fun logout() {
        scope.launch {
            staffService.signOut()
            appSettings.apply {
                username = ""
                pwd = ""
                clear()
            }
            _staff.value = null
        }
    }

    /**
     * Observes the list of conversations for the current staff member.
     * Conversations are sorted in descending order based on their last message time.
     */
    private fun observeConversations() {
        conversationObservationJob?.cancel()

        conversationObservationJob = scope.launch {
            staffService.observeConversations()
                .catch { error ->
                    _chatState.value = _chatState.value.copy(error = error.message)
                }
                .collect { conversations: List<Conversation> ->
                    val sortedConversations = conversations.sortedWith { a, b ->
                        val timeA = try {
                            if (a.lastMessageTime.contains("T") && a.lastMessageTime.endsWith("Z")) {
                                Date(a.lastMessageTime).getTime().toLong()
                            } else {
                                a.lastMessageTime.toLong()
                            }
                        } catch (e: Exception) {
                            0L
                        }
                        val timeB = try {
                            if (b.lastMessageTime.contains("T") && b.lastMessageTime.endsWith("Z")) {
                                Date(b.lastMessageTime).getTime().toLong()
                            } else {
                                b.lastMessageTime.toLong()
                            }
                        } catch (e: Exception) {
                            0L
                        }
                        timeB.compareTo(timeA)
                    }
                    _chatState.value = _chatState.value.copy(
                        conversations = sortedConversations
                    )
                }
        }
    }

    /**
     * Observes real-time updates for the staff member's profile and engagement stats.
     *
     * @param staffId The unique ID of the staff member.
     */
    private fun observeStaffEngagement(staffId: String) {
        scope.launch {
            staffService.observeStaff(staffId).collect { updatedStaff ->
                _staff.value = updatedStaff
            }
        }
    }

    /**
     * Selects a conversation to start chatting with a user.
     * Marks the conversation as read and begins observing messages for the selected user.
     *
     * @param selectedUser The conversation object representing the user to chat with.
     */
    fun selectConversation(selectedUser: Conversation) {
        _chatState.update {
            it.copy(
                selectedUser = selectedUser, messages = emptyList()
            )
        }


        messageObservationJob?.cancel()
        messageObservationJob = scope.launch {
            staffService.markConversationAsRead(selectedUser.userId)
            staffService.observeMessages(selectedUser.userId)
                .catch { error ->
                    _chatState.value = _chatState.value.copy(error = error.message)
                }
                .collect { messages ->
                    _chatState.value = _chatState.value.copy(messages = messages)
                }

        }
    }

    /**
     * Deselects the current conversation and stops observing messages.
     */
    fun deselectConversation() {
        _chatState.update {
            it.copy(selectedUser = null, messages = emptyList())
        }
        messageObservationJob?.cancel()
    }

    /**
     * Sends a text message to the currently selected user.
     *
     * @param messageText The text content of the message.
     */
    fun sendMessage(messageText: String) {
        val selectedUser = _chatState.value.selectedUser ?: return

        scope.launch {
            val api = staffService.sendMessage(messageText = messageText, userId = selectedUser.userId)
            when (api) {
                is ApiResult.Success -> {
                    _chatState.value = _chatState.value.copy(isLoading = false)
                }

                is ApiResult.Error -> {
                    _chatState.value = _chatState.value.copy(
                        isLoading = false,
                        error = api.exception.message
                    )
                }

                is ApiResult.Loading -> {
                    _chatState.value = _chatState.value.copy(isLoading = true)
                }
            }
        }
    }

    /**
     * Sends an image message to the currently selected user.
     *
     * @param imageData The raw image data (typically from the file picker).
     * @param caption Optional text caption for the image.
     */
    fun sendImageMessage(imageData: dynamic, caption: String = "") {
        val selectedUser = _chatState.value.selectedUser ?: return

        scope.launch {
            _chatState.value = _chatState.value.copy(isLoading = true)
            val api = staffService.sendImageMessage(
                imageData = imageData,
                caption = caption,
                userId = selectedUser.userId
            )
            when (api) {
                is ApiResult.Success -> {
                    _chatState.value = _chatState.value.copy(isLoading = false)
                }

                is ApiResult.Error -> {
                    _chatState.value = _chatState.value.copy(
                        isLoading = false,
                        error = api.exception.message
                    )
                }

                is ApiResult.Loading -> {
                    _chatState.value = _chatState.value.copy(isLoading = true)
                }
            }
        }
    }


    /**
     * Clears any existing error message in the chat state.
     */
    fun clearError() {
        _chatState.value = _chatState.value.copy(error = null)
    }

    /**
     * Toggles between dark and light themes for the application.
     */
    fun toggleTheme() {
        _chatState.update {
            it.copy(isDarkTheme = !it.isDarkTheme)
        }
    }


}
